/* ==========================================================================
   rs-calculator.js — Layer B. BMI + BMR, computed in the browser.
   SOURCE OF TRUTH: tools/seo/theme-bundle/assets/rs-calculator.js
   Installed by tools/seo/apply_theme_bundle.py. Never edit the theme copy.

   ══════════════════════════════════════════════════════════════════════════
   NOTHING LEAVES THE BROWSER. THAT IS AN ENFORCED PROPERTY, NOT A PROMISE.
   ══════════════════════════════════════════════════════════════════════════

   The page tells the reader that what they type stays on their device. This
   file is what has to make that true, so it is written to make the claim
   checkable by reading it:

     - no fetch, no XMLHttpRequest, no sendBeacon, no WebSocket, no dynamic
       import, no new Image().src, no form action, no navigation
     - no localStorage, no sessionStorage, no IndexedDB, no cookie write
     - no dataLayer push, no gtag, no fbq, no analytics call of any kind
     - no console logging of a field value
     - the <form> has no action and its submit is prevented, so no value can
       leave via a URL either

   Height, weight, age and sex are health-adjacent personal data. Under the
   Privacy Protection Law the cheapest possible posture is not to collect them
   at all, and a calculator that computes locally collects nothing: there is no
   record to secure, disclose, export or delete. SECURITY.md's rules about
   customer data are satisfied by there being no data.

   tools/seo/check_calculator_privacy.py greps the INSTALLED theme copy for
   every one of those APIs and fails the build if any appears. A promise in a
   comment is not a control; the grep is.

   ══════════════════════════════════════════════════════════════════════════
   WHAT IT DOES NOT COMPUTE
   ══════════════════════════════════════════════════════════════════════════

   No nutrient requirement, no intake target, no shortfall, no "you may be
   low in X", no product suggestion, and no activity multiplier on top of BMR.
   A tool that takes a person's input and returns a shortfall figure is an
   implied claim about a food, and that question is open in the counsel packet.
   BMI and BMR are arithmetic on numbers the user already knows about
   themselves; a shortfall is an assertion about what they should do next.

   No condition is ever named. The BMI result is a number plus the numeric
   band it falls in. The WHO category LABELS are deliberately absent: one of
   them names a diagnosed condition, and CLAUDE.md prohibits naming or
   targeting diagnosed conditions anywhere in RESET's copy. The bands are the
   same bands; only the medical nouns are gone.

   ══════════════════════════════════════════════════════════════════════════
   THE FORMULAS
   ══════════════════════════════════════════════════════════════════════════

   BMI   kg / m^2

   BMR   Mifflin-St Jeor, named on the page as required:
           male    10*kg + 6.25*cm - 5*age + 5
           female  10*kg + 6.25*cm - 5*age - 161

   Sex is an input because the formula takes one. It is a formula parameter,
   not a profile field, and it is not stored.

   All copy comes from data- attributes rendered by sections/rs-calculator
   .liquid, which sources it from the section schema. There is no Hebrew
   literal in this file, so no string can ship from here without passing
   tools/seo/lint_bundle_hebrew.py.
   ========================================================================== */
(function () {
  'use strict';

  // Guard rails on the inputs. Out-of-range does not compute: a BMI from a
  // 3 cm height is a number, and a number the tool prints is a number the
  // reader may believe.
  var LIMITS = {
    height: { min: 100, max: 250 },   // cm
    weight: { min: 30,  max: 300 },   // kg
    age:    { min: 18,  max: 120 }    // years; Mifflin-St Jeor is an adult formula
  };

  // BMI reference bands. Boundaries only. See above for why there are no names.
  var BMI_BANDS = [
    { max: 18.5 },
    { min: 18.5, max: 25 },
    { min: 25,   max: 30 },
    { min: 30 }
  ];

  function num(el) {
    if (!el) return NaN;
    var v = String(el.value == null ? '' : el.value).trim().replace(',', '.');
    if (v === '') return NaN;
    var n = Number(v);
    return isFinite(n) ? n : NaN;
  }

  function inRange(n, lim) {
    return isFinite(n) && n >= lim.min && n <= lim.max;
  }

  function bandIndex(bmi) {
    for (var i = 0; i < BMI_BANDS.length; i++) {
      var b = BMI_BANDS[i];
      var lo = (b.min === undefined) || bmi >= b.min;
      var hi = (b.max === undefined) || bmi < b.max;
      if (lo && hi) return i;
    }
    return -1;
  }

  function init(root) {
    if (root.getAttribute('data-rsc-ready') === '1') return;
    root.setAttribute('data-rsc-ready', '1');

    var mode    = root.getAttribute('data-rsc-mode');
    var form    = root.querySelector('[data-rsc-form]');
    var out     = root.querySelector('[data-rsc-result]');
    var valueEl = root.querySelector('[data-rsc-value]');
    var unitEl  = root.querySelector('[data-rsc-unit]');
    var descEl  = root.querySelector('[data-rsc-desc]');
    var errEl   = root.querySelector('[data-rsc-error]');
    var rows    = root.querySelectorAll('[data-rsc-band]');

    var fHeight = root.querySelector('[data-rsc-height]');
    var fWeight = root.querySelector('[data-rsc-weight]');
    var fAge    = root.querySelector('[data-rsc-age]');
    var fSex    = root.querySelector('[data-rsc-sex]');

    if (!form || !out || !valueEl) return;

    function clearBands() {
      for (var i = 0; i < rows.length; i++) rows[i].removeAttribute('data-rsc-active');
    }

    function fail(msg, fields) {
      out.hidden = false;
      valueEl.textContent = '';
      if (unitEl) unitEl.textContent = '';
      if (descEl) descEl.textContent = '';
      if (errEl) errEl.textContent = msg;
      clearBands();
      for (var i = 0; i < fields.length; i++) {
        if (fields[i]) fields[i].setAttribute('aria-invalid', 'true');
      }
    }

    function ok() {
      var all = [fHeight, fWeight, fAge];
      for (var i = 0; i < all.length; i++) {
        if (all[i]) all[i].removeAttribute('aria-invalid');
      }
      if (errEl) errEl.textContent = '';
    }

    function reset() {
      form.reset();
      out.hidden = true;
      valueEl.textContent = '';
      if (unitEl) unitEl.textContent = '';
      if (descEl) descEl.textContent = '';
      if (errEl) errEl.textContent = '';
      clearBands();
      ok();
    }

    function compute() {
      var h = num(fHeight);
      var w = num(fWeight);

      if (!inRange(h, LIMITS.height) || !inRange(w, LIMITS.weight)) {
        fail(root.getAttribute('data-rsc-msg-range'),
             [inRange(h, LIMITS.height) ? null : fHeight,
              inRange(w, LIMITS.weight) ? null : fWeight]);
        return;
      }

      if (mode === 'bmi') {
        var m = h / 100;
        var bmi = w / (m * m);
        ok();
        out.hidden = false;
        valueEl.textContent = bmi.toFixed(1);
        if (unitEl) unitEl.textContent = '';
        clearBands();
        var idx = bandIndex(bmi);
        var band = (idx >= 0 && rows[idx]) ? rows[idx] : null;
        if (band) {
          band.setAttribute('data-rsc-active', 'true');
          if (descEl) descEl.textContent = band.getAttribute('data-rsc-band-desc') || '';
        } else if (descEl) {
          descEl.textContent = '';
        }
        return;
      }

      var a = num(fAge);
      if (!inRange(a, LIMITS.age)) {
        fail(root.getAttribute('data-rsc-msg-age'), [fAge]);
        return;
      }
      var sex = fSex ? String(fSex.value) : '';
      if (sex !== 'male' && sex !== 'female') {
        fail(root.getAttribute('data-rsc-msg-range'), []);
        return;
      }
      // Mifflin-St Jeor.
      var bmr = (10 * w) + (6.25 * h) - (5 * a) + (sex === 'male' ? 5 : -161);
      ok();
      out.hidden = false;
      valueEl.textContent = String(Math.round(bmr));
      if (unitEl) unitEl.textContent = root.getAttribute('data-rsc-unit-label') || '';
      if (descEl) descEl.textContent = root.getAttribute('data-rsc-result-desc') || '';
    }

    form.addEventListener('submit', function (e) {
      // No action, no method, no navigation: the values cannot leave via a URL.
      e.preventDefault();
      compute();
    });

    var resetBtn = root.querySelector('[data-rsc-reset]');
    if (resetBtn) {
      resetBtn.addEventListener('click', function (e) {
        e.preventDefault();
        reset();
      });
    }
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-rsc-mode]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Shopify's theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', boot);
})();
